import { Typography } from '@material-ui/core';
import React, { ReactElement, useCallback } from 'react';
import { Controller } from 'react-hook-form';
import { Control, FieldError } from 'react-hook-form/dist/types';
import { RegisterOptions } from 'react-hook-form/dist/types/validator';

import { LabelWithUIGuidelines } from 'component-library/atoms/LabelWithUIGuidelines';
import { StyledTextField } from 'components/InputField';
import { FieldErrorText, InputContainer, TitleContainer } from './RHF.styles';

type RHFPhoneFieldProps = {
  name: string;
  control: Control;
  label: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  addons?: ReactElement;
  error?: FieldError;
  rules?: RegisterOptions;
};

/**
 * Champ téléphone compatible React Hook Form v6.
 * N'accepte que les chiffres (0-9) et le signe +.
 * Filtre les caractères non autorisés directement à la saisie.
 */
const RHFPhoneField = ({
  name,
  control,
  label,
  placeholder,
  required = false,
  disabled = false,
  addons,
  error,
  rules = {},
}: RHFPhoneFieldProps): JSX.Element => {
  /**
   * Bloque la saisie de caractères non autorisés.
   * Autorise uniquement : chiffres, +, et les touches de contrôle
   * (Backspace, Delete, Tab, flèches, Ctrl+A/C/V/X, etc.)
   */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const allowedKeys = [
        'Backspace',
        'Delete',
        'Tab',
        'Escape',
        'Enter',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Home',
        'End',
      ];

      // Autoriser les raccourcis clavier (Ctrl/Cmd + A, C, V, X)
      if (e.ctrlKey || e.metaKey) return;

      // Autoriser les touches de contrôle
      if (allowedKeys.includes(e.key)) return;

      // Autoriser les chiffres et le +
      if (/^[0-9+]$/.test(e.key)) return;

      // Bloquer tout le reste
      e.preventDefault();
    },
    [],
  );

  /**
   * Nettoie la valeur collée pour ne garder que les chiffres et le +.
   */
  const handleChange = useCallback(
    (
      e: React.ChangeEvent<HTMLInputElement>,
      rhfOnChange: (value: string) => void,
    ) => {
      const sanitized = e.target.value.replace(/[^0-9+]/g, '');
      rhfOnChange(sanitized);
    },
    [],
  );

  return (
    <InputContainer>
      <TitleContainer>
        <LabelWithUIGuidelines label={label} required={required} />
        {addons}
      </TitleContainer>
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ onChange, onBlur, value, ref }) => (
          <StyledTextField
            inputRef={ref}
            fullWidth
            type="tel"
            variant="outlined"
            size="small"
            placeholder={placeholder}
            value={value || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleChange(e, onChange)
            }
            onBlur={onBlur}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            error={!!error}
            inputProps={{
              name,
              inputMode: 'numeric',
              pattern: '[0-9+]*',
            }}
          />
        )}
      />
      {error && (
        <FieldErrorText>
          <Typography variant="caption" color="error">
            {error.message}
          </Typography>
        </FieldErrorText>
      )}
    </InputContainer>
  );
};

export default RHFPhoneField;
